import { declareStorageDomain } from '../../storage/storageGate';

const XIMI_ACCOUNTS_KEY = 'ximi.accounts';

const storage = declareStorageDomain({
    name: 'ximi-accounts',
    // Décision projet : la liste des comptes Ximi survit à la déconnexion
    // (seule la re-saisie du mot de passe est exigée). No-op explicite.
    onLogout: async () => {},
    // Bascule act-as : donnée d'appareil, indépendante de l'identité active — conservée.
    onIdentitySwitch: async () => {}
});

/** A scanned/known Ximi account, expressed in SSO terms (tenant-code + login_hint). */
export interface XimiAccount {
    tenantCode: string;
    appId?: string; // partie app du tenantCode (ex. `Master.XimiIndep`) — préfixe URL MobileAPI
    tenant?: string; // partie tenant du tenantCode (ex. `xmob`) — résolu serveur via le token
    loginHint: string;
    email?: string; // claim SSO `email` (minuscule) — ligne 2 de l'item
    displayName?: string; // `NOM, Prénom` (format Ximi) — hydraté au sign-in réussi (JWT identité)
    structureName?: string; // agencyName (Client/Agent) — affiché `[…]` après le nom
    structurePicture?: string; // data-URL logo structure — phase 2 (endpoint VM), placeholder sinon
    isCurrent?: boolean;
}

const sameAccount = (a: XimiAccount, b: XimiAccount): boolean =>
    a.tenantCode === b.tenantCode && a.loginHint === b.loginHint;

export async function getXimiAccounts(): Promise<XimiAccount[]> {
    return (await storage.getJSON<XimiAccount[]>(XIMI_ACCOUNTS_KEY)) ?? [];
}

export async function getCurrentXimiAccount(): Promise<XimiAccount | null> {
    const accounts = await getXimiAccounts();
    return accounts.find((account) => account.isCurrent) ?? null;
}

/**
 * Add or update an account and make it the current one. Merges over the existing
 * entry so optional fields (e.g. email/appId) survive an upsert that omits them.
 */
export async function upsertXimiAccount(account: XimiAccount): Promise<void> {
    const accounts = await getXimiAccounts();
    const existing = accounts.find((entry) => sameAccount(entry, account));
    const others = accounts.filter((entry) => !sameAccount(entry, account));
    const next = [
        ...others.map((entry) => ({ ...entry, isCurrent: false })),
        { ...existing, ...account, isCurrent: true }
    ];
    await storage.setJSON(XIMI_ACCOUNTS_KEY, next);
}

export async function setCurrentXimiAccount(tenantCode: string, loginHint: string): Promise<void> {
    const accounts = await getXimiAccounts();
    const next = accounts.map((account) => ({
        ...account,
        isCurrent: account.tenantCode === tenantCode && account.loginHint === loginHint
    }));
    await storage.setJSON(XIMI_ACCOUNTS_KEY, next);
}

export async function removeXimiAccount(tenantCode: string, loginHint: string): Promise<void> {
    const accounts = await getXimiAccounts();
    const next = accounts.filter(
        (account) => !(account.tenantCode === tenantCode && account.loginHint === loginHint)
    );
    await storage.setJSON(XIMI_ACCOUNTS_KEY, next);
}

export function clearXimiAccounts(): Promise<void> {
    return storage.remove(XIMI_ACCOUNTS_KEY);
}
